import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, ArrowLeft, Plus, Edit2, Trash2, Search, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import UserManagementModal from '@/components/modals/UserManagementModal';
import DeleteConfirmationModal from '@/components/modals/DeleteConfirmationModal';
import StatusTypeModal from '@/components/modals/StatusTypeModal';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000/api';

const AdminEmpresaPage = () => {
  const [users, setUsers] = useState([]);
  const [statusTypes, setStatusTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedStatusType, setSelectedStatusType] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isStatusTypeModalOpen, setIsStatusTypeModalOpen] = useState(false);
  const [empresa, setEmpresa] = useState(null);

  const { toast } = useToast();
  const { user: currentUser } = useAuth();

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Erro ao buscar usuários');
      const data = await response.json();
      // Filter users by current user's empresa
      const filteredUsers = data.filter(u => u.empresa_id === currentUser.empresa_id);
      setUsers(filteredUsers);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [currentUser.empresa_id, toast]);

  const fetchStatusTypes = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/status-types`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Erro ao buscar tipos de status');
      const data = await response.json();
      setStatusTypes(data);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  }, [toast]);

  useEffect(() => {
    fetchUsers();
    fetchStatusTypes();
  }, [fetchUsers, fetchStatusTypes]);

  const fetchEmpresa = useCallback(async () => {
    if (!currentUser?.empresa_id) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/admin/empresas?id=${currentUser.empresa_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Erro ao buscar empresa');
      const data = await response.json();
      setEmpresa(Array.isArray(data) ? data.find(e => e.id === currentUser.empresa_id) : data);
    } catch (error) {
      console.error('Erro ao buscar empresa:', error);
      setEmpresa(null);
    }
  }, [currentUser?.empresa_id]);

  useEffect(() => {
    fetchEmpresa();
  }, [fetchEmpresa]);

  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsUserModalOpen(true);
  };

  // Status default fixos
  const defaultStatusTypes = [
    { id: 'default-aberto', name: 'Aberto', type: '', color: '#38bdf8', isDefault: true },
    { id: 'default-concluido', name: 'Concluído', type: '', color: '#22c55e', isDefault: true }
  ];

  // Estado para saber em qual tipo está criando novo status
  const [newStatusTypeFor, setNewStatusTypeFor] = useState(null);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const onSaveUser = async (formData) => {
    try {
      const token = localStorage.getItem('auth_token');
      const method = selectedUser ? 'PUT' : 'POST';
      const url = selectedUser
        ? `${API_BASE_URL}/admin/users/${selectedUser.id}`
        : `${API_BASE_URL}/admin/users`;
      const dataToSend = { ...formData, empresa_id: currentUser.empresa_id };
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });
      if (!response.ok) throw new Error('Erro ao salvar usuário');
      const savedUser = await response.json();
      if (selectedUser) {
        setUsers(users.map(u => u.id === selectedUser.id ? savedUser : u));
        toast({ title: 'Sucesso', description: 'Usuário atualizado com sucesso.' });
      } else {
        setUsers([...users, savedUser]);
        toast({ title: 'Sucesso', description: 'Usuário criado com sucesso.' });
      }
      setIsUserModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  const handleCreateStatusType = () => {
    setSelectedStatusType(null);
    setIsStatusTypeModalOpen(true);
  };

  const handleEditStatusType = (statusType) => {
    setSelectedStatusType(statusType);
    setIsStatusTypeModalOpen(true);
  };

  const handleDeleteStatusType = (statusType) => {
    setSelectedStatusType(statusType);
    setIsDeleteModalOpen(true);
  };

  const onSaveStatusType = async (formData) => {
    // Se for status default, só atualiza cor localmente
    if (selectedStatusType && selectedStatusType.isDefault) {
      // Atualiza cor local dos default
      const updateDefault = defaultStatusTypes.map(ds =>
        ds.id === selectedStatusType.id ? { ...ds, color: formData.color } : ds
      );
      // Não salva no backend, só atualiza visual
      toast({ title: 'Cor atualizada', description: 'Cor do status default alterada localmente.' });
      setIsStatusTypeModalOpen(false);
      setSelectedStatusType(null);
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      const method = selectedStatusType && selectedStatusType.id ? 'PUT' : 'POST';
      const url = selectedStatusType && selectedStatusType.id
        ? `${API_BASE_URL}/status-types/${selectedStatusType.id}`
        : `${API_BASE_URL}/status-types`;
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Erro ao salvar tipo de status');
      const savedStatusType = await response.json();
      if (selectedStatusType && selectedStatusType.id) {
        setStatusTypes(statusTypes.map(st => st.id === selectedStatusType.id ? savedStatusType : st));
        toast({ title: 'Sucesso', description: 'Tipo de status atualizado com sucesso.' });
      } else {
        setStatusTypes([...statusTypes, savedStatusType]);
        toast({ title: 'Sucesso', description: 'Tipo de status criado com sucesso.' });
      }
      setIsStatusTypeModalOpen(false);
      setSelectedStatusType(null);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  const onDeleteStatusType = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/status-types/${selectedStatusType.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Erro ao excluir tipo de status');
      setStatusTypes(statusTypes.filter(st => st.id !== selectedStatusType.id));
      toast({ title: 'Sucesso', description: 'Tipo de status excluído com sucesso.' });
      setIsDeleteModalOpen(false);
      setSelectedStatusType(null);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  const onDeleteUser = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/admin/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao excluir usuário');
      }
      setUsers(users.filter(u => u.id !== selectedUser.id));
      toast({ title: 'Sucesso', description: 'Usuário excluído com sucesso.' });
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>Administração da Empresa - VHSYS</title>
      </Helmet>
      <div className="min-h-screen bg-slate-900 flex">
        {/* Sidebar */}
        <aside className="hidden md:flex flex-col w-64 bg-slate-800 border-r border-slate-700 p-6 gap-8 shadow-xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-orange-500 rounded-full shadow-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">{empresa?.nome?.charAt(0)?.toUpperCase() || 'E'}</span>
            </div>
            <span className="text-xl font-bold text-white tracking-wide">{empresa?.nome || 'Administração'}</span>
          </div>
          <nav className="flex flex-col gap-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-300 hover:text-orange-400 transition-colors font-medium">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
            <span className="flex items-center gap-2 text-slate-300">
              <Users className="w-4 h-4" /> Usuários
            </span>
            <span className="flex items-center gap-2 text-slate-300">
              <Settings className="w-4 h-4" /> Status
            </span>
          </nav>
          <div className="mt-auto flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-500 text-white font-semibold hover:ring-2 hover:ring-orange-400 transition-all">
              {currentUser?.name?.charAt(0)?.toUpperCase()}
            </div>
            <span className="text-slate-200 text-sm">{currentUser?.name}</span>
          </div>
        </aside>
        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center justify-start px-2 sm:px-8 py-8">
          {/* Header Moderno */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-6xl mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-4xl font-extrabold text-white drop-shadow-lg">Administração da Empresa</h1>
                <p className="text-slate-400 mt-2 text-lg">Gerencie usuários e status personalizados</p>
              </div>
            </div>
          </motion.div>
          {/* Usuários Card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-6xl bg-slate-700 rounded-2xl shadow-2xl border border-slate-600 p-8 mb-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <Users className="w-7 h-7 text-orange-500" />
                <h2 className="text-2xl font-bold text-white">Usuários da Empresa</h2>
              </div>
              <Button className="bg-orange-500 hover:bg-orange-600 shadow-lg" onClick={handleCreateUser}>
                <Plus className="w-5 h-5 mr-2" /> Novo Usuário
              </Button>
            </div>
            <div className="relative w-full md:w-72 mb-6">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar usuários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-slate-600 border border-slate-500 rounded-xl pl-10 pr-4 py-2 text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 shadow-md"
              />
            </div>
            {loading ? (
              <div className="text-center py-8 text-slate-400 animate-pulse">Carregando...</div>
            ) : (
              <div className="overflow-x-auto rounded-xl">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-600 bg-slate-800">
                      <th className="text-left py-3 px-4 text-slate-200 font-semibold">Nome</th>
                      <th className="text-left py-3 px-4 text-slate-200 font-semibold">Email</th>
                      <th className="text-left py-3 px-4 text-slate-200 font-semibold">Função</th>
                      <th className="text-left py-3 px-4 text-slate-200 font-semibold">Criado em</th>
                      <th className="text-left py-3 px-4 text-slate-200 font-semibold">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b border-gray-300 odd:bg-gray-100 even:bg-white odd:text-black even:text-black hover:bg-gray-200/50 transition-all">
                        <td className="py-3 px-4 text-black font-medium">{user.name}</td>
                        <td className="py-3 px-4 text-black">{user.email}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold shadow-md ${
                            user.role === 'superadmin' ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white' :
                            user.role === 'admin' ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white' :
                            'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-black">
                          {new Date(user.created_at).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="border-blue-500 text-blue-500 hover:bg-blue-500/10" onClick={() => handleEditUser(user)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="border-red-500 text-red-500 hover:bg-red-500/10" onClick={() => handleDeleteUser(user)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredUsers.length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    {searchTerm ? 'Nenhum usuário encontrado para a busca.' : 'Nenhum usuário cadastrado.'}
                  </div>
                )}
              </div>
            )}
          </motion.div>
          {/* Status Types Moderno */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="w-full max-w-6xl bg-slate-700 rounded-2xl shadow-2xl border border-slate-600 p-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <Settings className="w-7 h-7 text-orange-500" />
                <h2 className="text-2xl font-bold text-white">Tipos de Status</h2>
              </div>
              <Button className="bg-orange-500 hover:bg-orange-600 shadow-lg" onClick={() => { setNewStatusTypeFor(null); setSelectedStatusType(null); setIsStatusTypeModalOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Novo Status
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {['meta', 'servico', 'atendimento', 'implementacao'].map(type => {
                const typeStatusTypes = statusTypes.filter(st => st.type === type);
                const typeName = {
                  meta: 'Metas',
                  servico: 'Serviços',
                  atendimento: 'Implantações',
                  implementacao: 'Visitas/Treinamentos'
                }[type];
                const typeColor = {
                  meta: 'from-purple-500 to-indigo-500',
                  servico: 'from-green-500 to-teal-500',
                  atendimento: 'from-yellow-500 to-orange-500',
                  implementacao: 'from-pink-500 to-red-500'
                }[type];
                // Status default + custom
                const allStatusTypes = [
                  ...defaultStatusTypes.map(ds => ({ ...ds, type })),
                  ...typeStatusTypes
                ];
                return (
                  <div key={type} className={`bg-slate-600 rounded-xl p-6 shadow-lg border border-slate-500 flex flex-col gap-4`}>
                    <h3 className={`text-lg font-bold text-white bg-gradient-to-r ${typeColor} bg-clip-text text-transparent`}>{typeName}</h3>
                    <div className="flex flex-wrap gap-2">
                      {allStatusTypes.map(st => (
                        <div key={st.id} className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-md animate-in fade-in duration-300`} style={{ background: st.color || undefined, backgroundImage: st.isDefault ? undefined : undefined }}>
                          <span className="text-white text-sm font-semibold drop-shadow">{st.name}</span>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-white hover:bg-white/10" onClick={() => { setSelectedStatusType(st); setIsStatusTypeModalOpen(true); }}>
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            {!st.isDefault && (
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-white hover:bg-red-500/20" onClick={() => handleDeleteStatusType(st)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      {allStatusTypes.length === 0 && (
                        <div className="text-slate-400 text-sm">Nenhum status configurado</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
          {/* Modais */}
          <UserManagementModal
            isOpen={isUserModalOpen}
            onClose={() => {
              setIsUserModalOpen(false);
              setSelectedUser(null);
            }}
            onSave={onSaveUser}
            user={selectedUser}
          />
          <StatusTypeModal
            isOpen={isStatusTypeModalOpen}
            onClose={() => {
              setIsStatusTypeModalOpen(false);
              setSelectedStatusType(null);
              setNewStatusTypeFor(null);
            }}
            onSave={async (formData) => {
              // Se for novo status, garantir que type está correto
              if (!formData.type && formData.type !== '') {
                formData.type = formData.type;
              } else if (!formData.type && newStatusTypeFor) {
                formData.type = newStatusTypeFor;
              }
              await onSaveStatusType(formData);
              setNewStatusTypeFor(null);
            }}
            statusType={selectedStatusType}
          />
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false);
              setSelectedUser(null);
            }}
            onConfirm={selectedStatusType ? onDeleteStatusType : onDeleteUser}
            title={selectedStatusType ? "Excluir Tipo de Status" : "Excluir Usuário"}
            description={selectedStatusType 
              ? `Tem certeza que deseja excluir o status "${selectedStatusType?.name}"?` 
              : `Tem certeza que deseja excluir o usuário "${selectedUser?.name}"? Esta ação não pode ser desfeita.`
            }
          />
        </main>
      </div>
    </>
  );
};

export default AdminEmpresaPage;